import { useMemo } from 'react'
import { useFieldArray, useFormState, useWatch } from 'react-hook-form'

import { useFormWithZod } from '#/shared/lib/form'
import {
  EMPTY_VEHICLE,
  EMPTY_VEHICLES_DEFAULTS,
  useVehiclesSchema,
} from './vehicles-schema'
import type { VehicleItem, VehiclesFormData } from './vehicles-schema'

const YEAR_PATTERN = /^(19|20)\d{2}$/

function isVehicleValid(v: VehicleItem | undefined): boolean {
  if (!v) return false
  return (
    YEAR_PATTERN.test(v.year) &&
    v.make.trim().length > 0 &&
    v.model.trim().length > 0
  )
}

interface UseVehiclesFormParams {
  defaultValues?: Partial<VehiclesFormData>
  onSubmit?: (data: VehiclesFormData) => void | Promise<void>
}

export function useVehiclesForm({
  defaultValues,
  onSubmit,
}: UseVehiclesFormParams = {}) {
  const schema = useVehiclesSchema()
  const values = useMemo<VehiclesFormData>(
    () => ({ ...EMPTY_VEHICLES_DEFAULTS, ...defaultValues }),
    [defaultValues],
  )
  const form = useFormWithZod(schema, {
    defaultValues: values,
    values,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { errors, dirtyFields } = useFormState({ control: form.control })
  const isDirty = Object.keys(dirtyFields).length > 0

  const fieldArray = useFieldArray({
    control: form.control,
    name: 'vehicles',
  })

  const watchedVehicles = useWatch({
    control: form.control,
    name: 'vehicles',
  })
  const lastVehicle = watchedVehicles[watchedVehicles.length - 1]
  const canAddVehicle = isVehicleValid(lastVehicle as VehicleItem | undefined)

  return {
    control: form.control,
    register: form.register,
    setValue: form.setValue,
    getValues: form.getValues,
    trigger: form.trigger,
    errors,
    isSubmitting: form.isSubmitting,
    isDirty,
    handleSubmit: onSubmit ? form.handleSubmit(onSubmit) : undefined,
    reset: form.reset,
    fields: fieldArray.fields,
    canAddVehicle,
    addVehicle: async () => {
      if (canAddVehicle) {
        fieldArray.append({ ...EMPTY_VEHICLE })
        return
      }
      const lastIndex = fieldArray.fields.length - 1
      await form.trigger([
        `vehicles.${lastIndex}.year`,
        `vehicles.${lastIndex}.make`,
        `vehicles.${lastIndex}.model`,
      ])
    },
    removeVehicle: (index: number) => fieldArray.remove(index),
  }
}
